import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  List,
  ListItem,
  ListIcon,
  Link,
  Badge,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaCheckCircle, FaUniversalAccess } from 'react-icons/fa';

const AccessibilityStatement = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const lastUpdated = 'January 2025';

  return (
    <Box>
      {/* Hero Section */}
      <Box
        bg="linear-gradient(120deg, #14B7D6 0%, #1E9BE8 46%, #2E7CF0 100%)"
        color="white"
        pt={{ base: '130px', lg: '190px' }}
        pb={{ base: 12, lg: 16 }}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <FaUniversalAccess size={60} />
            <Heading size="2xl" fontWeight="800">
              Accessibility Statement
            </Heading>
            <Text fontSize="lg" opacity={0.95}>
              Lake Lucien Surgery Center is committed to ensuring digital accessibility for all people
            </Text>
            <Badge colorScheme="green" fontSize="md" px={4} py={2}>
              WCAG 2.1 Level AA Compliant
            </Badge>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.lg" py={12}>
        <VStack spacing={8} align="stretch">
          
          {/* Commitment */}
          <Box bg={bgColor} p={8} borderRadius="lg" boxShadow="md">
            <Heading size="lg" mb={4} color="brand.700">
              Our Commitment
            </Heading>
            <Text fontSize="md" lineHeight="tall" color="gray.700" mb={4}>
              Lake Lucien Surgery Center is committed to ensuring that our website is accessible to people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards.
            </Text>
            <Text fontSize="md" lineHeight="tall" color="gray.700">
              We believe that healthcare information should be accessible to all individuals, regardless of their abilities or the technologies they use to access the web.
            </Text>
          </Box>

          {/* Standards */}
          <Box bg={bgColor} p={8} borderRadius="lg" boxShadow="md">
            <Heading size="lg" mb={4} color="brand.700">
              Conformance Standards
            </Heading>
            <Text fontSize="md" lineHeight="tall" color="gray.700" mb={4}>
              Our website strives to conform to the following accessibility standards:
            </Text>
            <List spacing={3}>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="green.500" />
                <strong>WCAG 2.1 Level AA</strong> - Web Content Accessibility Guidelines
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="green.500" />
                <strong>WCAG 2.2</strong> - Latest accessibility guidelines
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="green.500" />
                <strong>ADA</strong> - Americans with Disabilities Act
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="green.500" />
                <strong>Section 508</strong> - Federal accessibility standards
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="green.500" />
                <strong>AODA</strong> - Accessibility for Ontarians with Disabilities Act
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="green.500" />
                <strong>EN 301 549</strong> - European accessibility standard
              </ListItem>
            </List>
          </Box>

          {/* Features */}
          <Box bg={bgColor} p={8} borderRadius="lg" boxShadow="md">
            <Heading size="lg" mb={4} color="brand.700">
              Accessibility Features
            </Heading>
            <Text fontSize="md" lineHeight="tall" color="gray.700" mb={4}>
              Our website includes the following accessibility features:
            </Text>
            <List spacing={3}>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                <strong>Custom Accessibility Widget</strong> - Comprehensive tools for personalized accessibility
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                <strong>High Contrast Mode</strong> - Enhanced visibility for low vision users
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                <strong>Text Sizing</strong> - Adjustable font sizes (50% - 150%)
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                <strong>Text Spacing</strong> - Adjustable letter and word spacing
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                <strong>Dyslexia-Friendly Fonts</strong> - Legible font options
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                <strong>Link Highlighting</strong> - Enhanced link visibility
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                <strong>Animation Controls</strong> - Pause or disable animations
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                <strong>Keyboard Navigation</strong> - Full keyboard accessibility
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                <strong>Screen Reader Compatibility</strong> - Optimized for NVDA, JAWS, and VoiceOver
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                <strong>Color Adjustments</strong> - Saturation controls for color sensitivity
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                <strong>Persistent Settings</strong> - Your preferences are saved automatically
              </ListItem>
            </List>
          </Box>

          {/* Technical Specifications */}
          <Box bg={bgColor} p={8} borderRadius="lg" boxShadow="md">
            <Heading size="lg" mb={4} color="brand.700">
              Technical Specifications
            </Heading>
            <Text fontSize="md" lineHeight="tall" color="gray.700" mb={4}>
              Our website is designed to be compatible with:
            </Text>
            <List spacing={3}>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Modern web browsers (Chrome, Firefox, Safari, Edge)
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Screen readers (NVDA, JAWS, VoiceOver, TalkBack)
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Voice recognition software
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Screen magnification software
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Alternative input devices
              </ListItem>
            </List>
          </Box>

          {/* Measures */}
          <Box bg={bgColor} p={8} borderRadius="lg" boxShadow="md">
            <Heading size="lg" mb={4} color="brand.700">
              Measures to Support Accessibility
            </Heading>
            <Text fontSize="md" lineHeight="tall" color="gray.700" mb={4}>
              Lake Lucien Surgery Center takes the following measures to ensure accessibility:
            </Text>
            <List spacing={3}>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Include accessibility as part of our mission statement
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Integrate accessibility into our procurement practices
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Provide continual accessibility training for our staff
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Assign clear accessibility goals and responsibilities
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Employ formal accessibility quality assurance methods
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Use in-house accessibility tools and processes
              </ListItem>
            </List>
          </Box>

          {/* Limitations */}
          <Box bg="orange.50" p={8} borderRadius="lg" borderLeft="4px solid" borderColor="orange.500">
            <Heading size="md" mb={4} color="orange.700">
              Known Limitations
            </Heading>
            <Text fontSize="md" lineHeight="tall" color="gray.700" mb={4}>
              Despite our best efforts to ensure accessibility, there may be some limitations:
            </Text>
            <List spacing={3}>
              <ListItem fontSize="sm" color="gray.700">
                • Third-party content (such as embedded videos) may not be fully accessible
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                • Some PDF documents may not be fully accessible (we are working to remediate these)
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                • Legacy content may not meet current accessibility standards (we are updating these progressively)
              </ListItem>
            </List>
          </Box>

          <Divider />

          {/* Feedback */}
          <Box bg="blue.50" p={8} borderRadius="lg" borderLeft="4px solid" borderColor="brand.500">
            <Heading size="md" mb={4} color="brand.700">
              Feedback & Contact
            </Heading>
            <Text fontSize="md" lineHeight="tall" color="gray.700" mb={4}>
              We welcome your feedback on the accessibility of Lake Lucien Surgery Center's website. Please let us know if you encounter accessibility barriers:
            </Text>
            <List spacing={3}>
              <ListItem fontSize="sm" color="gray.700">
                <strong>Phone:</strong>{' '}
                <Link href="tel:+13213213935" color="brand.600">
                  (321) 321-3935
                </Link>
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                <strong>Email:</strong>{' '}
                <Link href="mailto:info@lucienasc.com" color="brand.600">
                  info@lucienasc.com
                </Link>
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                <strong>Address:</strong> 2200 Lucien Way, Suite 100, Maitland, FL 32751
              </ListItem>
            </List>
            <Text fontSize="sm" color="gray.600" mt={4}>
              We aim to respond to accessibility feedback within 2 business days.
            </Text>
          </Box>

          {/* Assessment */}
          <Box bg={bgColor} p={8} borderRadius="lg" boxShadow="md">
            <Heading size="lg" mb={4} color="brand.700">
              Assessment Approach
            </Heading>
            <Text fontSize="md" lineHeight="tall" color="gray.700" mb={4}>
              Lake Lucien Surgery Center assessed the accessibility of this website by the following approaches:
            </Text>
            <List spacing={3}>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Self-evaluation using automated accessibility testing tools
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Manual testing with keyboard navigation
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Screen reader testing (NVDA, JAWS, VoiceOver)
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                Review against WCAG 2.1 Level AA criteria
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="brand.500" />
                User testing with individuals with disabilities
              </ListItem>
            </List>
          </Box>

          {/* Footer */}
          <Box pt={6} borderTop="1px solid" borderColor="gray.200" textAlign="center">
            <Text fontSize="sm" color="gray.500">
              <strong>Date of Statement:</strong> {lastUpdated}
            </Text>
            <Text fontSize="sm" color="gray.500" mt={2}>
              This statement was last reviewed and updated on {lastUpdated}
            </Text>
            <Text fontSize="xs" color="gray.400" mt={4}>
              This accessibility statement applies to{' '}
              <Link href="https://lucienasc.com" color="brand.600">
                lucienasc.com
              </Link>
            </Text>
          </Box>

        </VStack>
      </Container>
    </Box>
  );
};

export default AccessibilityStatement;
